<template>
  <d-modal @close="handleClose" size="lg">
    <d-modal-header>
      <d-modal-title>
        {{ $t('notifications.addNewNotification') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body class="px-4">
      <validation-observer ref="notification-new-form" tag="form">
        <div class="form-group">
          <label for="name">
            {{ $t('notifications.form.description') }}:
          </label>

          <validation-provider
            rules="required"
            name="description"
            v-slot="{ errors }">
            <d-textarea
              v-model="description"
              :value.prop="description"
              :state="errors.length ? 'invalid' : null"
              :rows="5" />
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <d-row>
          <d-col cols="2">
            <div class="form-group">
              <label for="name">
                {{ $t('notifications.form.date') }}:
              </label>

              <validation-provider
                rules="required"
                name="date"
                v-slot="{ errors }">
                <d-datepicker
                  v-model="date"
                  :monday-first="true"
                  format="yyyy-MM-dd"
                  :placeholder="$t('ui.chooseDate')"
                  :disabled-dates="pastDates"
                  :language="translationsForDatePicker" />
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>
          </d-col>

          <d-col cols="3">
            <div class="form-group">
              <label for="name">
                {{ $t('notifications.form.time') }}:
              </label>

              <div class="d-flex align-middle">
                <d-form-select
                  v-model="timeHours"
                  class="mr-2">
                  <option
                    v-for="(hour, index) of hoursList"
                    :key="'time-hour-' + index"
                    :value="hour">
                    {{ hour }}
                  </option>
                </d-form-select>

                <span style="line-height: 30px;">:</span>

                <d-form-select
                  v-model="timeMinutes"
                  class="ml-2">
                  <option
                    v-for="(minute, index) of minutesList"
                    :key="'time-minute-' + index"
                    :value="minute">
                    {{ minute }}
                  </option>
                </d-form-select>
              </div>
            </div>
          </d-col>

          <d-col cols="2">
            <div class="form-group">
              <label>
                {{ $t('notifications.form.isRepeatable') }}
              </label>

              <div>
                <d-checkbox
                  inline
                  v-model="isRepeatable"
                  toggle />
              </div>
            </div>
          </d-col>

          <d-col
            v-if="isRepeatable"
            cols="2">
            <div class="form-group">
              <label>
                {{ $t('notifications.form.frequency') }}:
              </label>

              <div>
                <validation-provider
                  rules="required"
                  name="frequency"
                  v-slot="{ errors }">
                  <d-form-select
                    v-model="frequency"
                    :state="errors.length ? 'invalid' : null"
                    :options="[{
                      text: $t('notifications.form.frequencyValues.selectValue'),
                      value: null
                    },{
                      text: $t('notifications.form.frequencyValues.daily'),
                      value: 'DAILY'
                    }, {
                      text: $t('notifications.form.frequencyValues.weekly'),
                      value: 'WEEKLY'
                    }, {
                      text: $t('notifications.form.frequencyValues.monthly'),
                      value: 'MONTHLY'
                    }]" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </div>
          </d-col>

          <d-col
            v-if="isRepeatable"
            cols="3">
            <div class="form-group">
              <label>
                {{ $t('notifications.form.repeatNumber') }}:
              </label>

              <div>
                <validation-provider
                  rules="required|only-integer-numbers"
                  name="repeat-number"
                  v-slot="{ errors }">
                  <d-input
                    type="number"
                    min="0"
                    step="1"
                    :state="errors.length ? 'invalid' : null"
                    v-model="repeatNumber" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>

                <small class="note text-light">
                  {{ $t('notifications.form.repeatNumberNote') }}
                </small>
              </div>
            </div>
          </d-col>
        </d-row>

        <d-row>
          <d-col cols="3">
            <div class="form-group">
              <label>
                {{ $t('notifications.form.showInDashboard') }}:
              </label>

              <div>
                <d-checkbox
                  inline
                  v-model="showInDashboard"
                  toggle />
              </div>
            </div>
          </d-col>

          <d-col
            v-if="showInDashboard || sendReminder"
            cols="6">
            <div class="form-group">
              <label>
                {{ $t('notifications.form.reminderBefore') }}
              </label>

              <validation-provider
                rules="required|only-integer-numbers"
                name="reminder-before"
                v-slot="{ errors }">
                <d-input-group>
                  <d-input
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                    class="text-right"
                    :state="errors.length ? 'invalid' : null"
                    v-model="reminderBefore" />
                  <d-input-group-text slot="append">
                    h
                  </d-input-group-text>
                </d-input-group>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>
          </d-col>
        </d-row>

        <d-row>
          <d-col cols="3">
            <div class="form-group">
              <label>
                {{ $t('notifications.form.sendReminder') }}:
              </label>

              <div>
                <d-checkbox
                  inline
                  v-model="sendReminder"
                  toggle />
              </div>
            </div>
          </d-col>

          <d-col
            v-if="sendReminder"
            cols="9">
            <div
              class="form-group mt-3"
              style="width: 504px;">
              <label class="d-flex">
                <span class="pt-1">{{ $t('notifications.form.emailCarbonCopy') }}:</span>

                <d-button
                  theme="success"
                  class="ml-auto"
                  @click.prevent="emailCarbonCopy.push('');">
                  <i class="material-icons">add</i>
                  {{ $t('notifications.form.addCC') }}
                </d-button>
              </label>

              <div
                v-for="(email, index) of emailCarbonCopy"
                :key="'email-cc-' + index"
                class="d-flex mb-1">
                <validation-provider
                  rules="required|email"
                  name="email-carbon-copy"
                  v-slot="{ errors }">
                  <d-input
                    v-model="emailCarbonCopy[index]"
                    :state="errors.length ? 'invalid' : null"
                    style="width: 450px" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
                <d-button
                  theme="danger"
                  @click.prevent="removeEmailCarbonCopy(index)"
                  class="ml-2">
                  <i class="material-icons">delete</i>
                </d-button>
              </div>
            </div>
          </d-col>
        </d-row>
      </validation-observer>
    </d-modal-body>
    <d-modal-footer>
      <d-button
        class="w-50 ml-auto mr-auto"
        theme="success"
        @click.prevent="validateForm"
        :disabled="submitInProgress">
        {{ $t('notifications.addNewNotification') }}
      </d-button>
    </d-modal-footer>
  </d-modal>
</template>

<script>
import datepickerTranslations from './../../data/datepicker-translations.js';
import FormUtils from '../../utils/FormUtils.js';
import getPrefferedLanguage from './../../utils/i18nUtils.js';

export default {
  name: 'order-notification-new-popup',
  props: [
    'orderID'
  ],
  computed: {
    hoursList () {
      return Array.from({ length: 24 }, (v, k) => k < 10 ? '0' + k : (k).toString());
    },
    minutesList () {
      return Array.from({ length: 60 }, (v, k) => k < 10 ? '0' + k : (k).toString());
    },
    translationsForDatePicker () {
      return datepickerTranslations[getPrefferedLanguage()];
    },
    pastDates () {
      let yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      return {
        to: yesterday
      };
    }
  },
  data () {
    return {
      description: '',
      date: new Date(),
      frequency: null,
      isRepeatable: false,
      repeatNumber: 0,
      timeHours: '00',
      timeMinutes: '00',
      showInDashboard: false,
      sendReminder: false,
      reminderBefore: 1,
      emailCarbonCopy: [],
      // ui flags
      submitInProgress: false
    };
  },
  methods: {
    validateForm () {
      FormUtils.validate(this.$refs['notification-new-form'], this.submitForm);
    },
    submitForm () {
      this.submitInProgress = true;

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/notifications/store',
        formData: this.prepareFormData(),
        successTitle: this.$t('notifications.form.addNotificationSuccessTitle'),
        successText: this.$t('notifications.form.addNotificationSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('notifications.form.addNotificationErrorText'),
        successAction: () => {
          this.submitInProgress = false;
          this.$bus.$emit('notifications-list-reload-needed');
          this.handleClose();
        },
        failAction: () => {
          this.submitInProgress = false;
        }
      });
    },
    prepareFormData () {
      let formData = {
        description: this.description,
        date: [this.date.getFullYear(), this.date.getMonth() + 1, this.date.getDate()].map(n => n < 10 ? '0' + n : n).join('-'),
        time: [this.timeHours, this.timeMinutes].join(':'),
        show_in_dashboard: +this.showInDashboard,
        send_reminder: +this.sendReminder,
        is_repeatable: +this.isRepeatable,
        order_id: +this.orderID
      };

      if (this.showInDashboard || this.sendReminder) {
        formData.reminder_before = this.reminderBefore;
      }

      if (this.sendReminder) {
        formData.email_carbon_copy = this.emailCarbonCopy.join(',');
      }

      if (this.isRepeatable) {
        formData.frequency = this.frequency;
        formData.repeat_number = this.repeatNumber;
      }

      return formData;
    },
    removeEmailCarbonCopy (index) {
      this.emailCarbonCopy.splice(index, 1);
    },
    handleClose () {
      this.$bus.$emit('close-order-notification-new-popup');
    }
  }
};
</script>
